import _ from 'lodash';
import { compose } from 'redux';
import { ErrorHandler, InfiniteScrollComponent, usePaginatedQuery } from '@playbookux/playbook-web-core';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';

import {
  Container,
  HeaderContainer,
  Dropdown,
  InputContainer,
  Header,
} from './styles';
import {
  dropdownItems as options,
  DEBOUNCE_TIME,
  STATUS_OPTIONS,
  TITLE,
} from './constants';
import Search from '../../../core/layout-components/search';
import ChangeStatusModal from './change-status-modal';
import CustomTable from './custom-table';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';
import useDebounce from '../../../core/hooks/use-debounce';

import { GET_PRACTICE_TESTS, LIMIT } from '../../../core/graphql/test-sessions';
import { UPDATE_STATUS } from './graphql';

const PracticeTests = () => {
  const [updatingError, setUpdatingError] = useState();
  const [showModal, setShowModal] = useState(false);
  const [statusOptions, setStatusOptions] = useState(STATUS_OPTIONS);
  const [currentElement, setCurrentElement] = useState();
  const [currentStatus, setCurrentStatus] = useState('All');
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(() => search, DEBOUNCE_TIME, [search]);

  const attributes = {
    variables: {
      limit: LIMIT,
      offset: 0,
      status: currentStatus,
      search: debouncedSearch,
    },
  };

  const {
    data = {},
    fetchMore,
    loading,
    error,
    refetch,
    hasMore,
  } = usePaginatedQuery(GET_PRACTICE_TESTS, attributes, { dataField: 'practiceTests' });

  const { practiceTests = [] } = data;
  const [practiceTestsData, setPracticeTestsData] = useState([]);

  const handleStatusFilter = (event, result) => {
    const { name, value } = result || event.target;
    if (name && value) {
      setCurrentStatus({ currentStatus, [name]: value });
    }
  };

  const currentPracticeTest = useMemo(() => (
    currentElement && !_.isEmpty(practiceTests)
      ? _.find(practiceTests, { id: currentElement })
      : {}
  ), [currentElement, practiceTests]);

  const [updatePracticeTestStatus, {
    loading: updating,
  }] = useMutation(UPDATE_STATUS);

  const handleUpdateStatus = async () => {
    try {
      const { value } = _.find(statusOptions, { checked: true });

      if (currentPracticeTest.practiceTestStatus !== value) {
        await updatePracticeTestStatus({
          variables: {
            id: currentElement,
            status: value,
          },
        });

        setShowModal(false);
        setUpdatingError(null);
        setStatusOptions(STATUS_OPTIONS);
      }
    } catch (err) {
      setUpdatingError(err);
    }
  };

  const handleClickOption = (key) => {
    const updatedOptions = _.map(statusOptions, (option) => ({
      ...option,
      checked: option.key === key,
    }));

    setStatusOptions(updatedOptions);
  };

  const handleClose = () => {
    setCurrentElement(null);
    setShowModal(false);
    setStatusOptions(STATUS_OPTIONS);
  };

  const handleModal = (id) => {
    setCurrentElement(id);
    setShowModal(!!id);
  };

  const handleOpenVideo = (projectId, testerId) => {
    const win = window.open(`/projects/${projectId}/videos/${testerId}`, '_blank');
    win.focus();
  };

  const handleSearchChange = ({ target }) => {
    setSearch(_.trim(target.value));
  };

  useEffect(() => {
    if (!_.isEmpty(currentPracticeTest)) {
      const updatedOptions = _.map(statusOptions, (option) => ({
        ...option,
        checked: currentPracticeTest.practiceTestStatus === option.value,
      }));

      setStatusOptions(updatedOptions);
    }
  }, [currentPracticeTest]);

  useEffect(() => {
    if (!loading) {
      setPracticeTestsData(practiceTests);
    }
  },
  [practiceTests]);

  // refetch() is used to redo the query if currentStatus changes
  useEffect(() => {
    if (!loading) {
      refetch();
    }
  }, [currentStatus]);

  if (error) {
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving practice tests."
        />
      </Container>
    );
  }

  return (
    <Container>
      <HeaderContainer>
        <Header as="h1">
          {TITLE}
        </Header>

        <InputContainer>
          <Dropdown
            placeholder="Practice Test Status"
            selection
            scrolling
            onChange={handleStatusFilter}
            options={options}
            value={currentStatus}
          >
            <Dropdown.Menu>
              {options.map((status) => (
                <Dropdown.Item
                  onClick={() => setCurrentStatus(status.value)}
                  text={status.text}
                  value={status.value}
                  key={status.key}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Search
            input={{ icon: 'search', iconPosition: 'left' }}
            placeholder="Search by email"
            onSearchChange={handleSearchChange}
            value={search || ''}
            showNoResults={false}
            loading={loading && search}
          />
        </InputContainer>
      </HeaderContainer>

      <InfiniteScrollComponent
        useWindow
        hasMore={hasMore}
        onLoadMore={fetchMore}
      >
        <Container>
          <ChangeStatusModal
            error={updatingError}
            loading={updating}
            onClick={handleClickOption}
            onClose={handleClose}
            onSubmit={handleUpdateStatus}
            options={statusOptions}
            show={showModal}
          />
          <CustomTable
            practiceTests={practiceTestsData}
            onClick={handleModal}
            onOpenVideo={handleOpenVideo}
          />
        </Container>
      </InfiniteScrollComponent>
    </Container>
  );
};

PracticeTests.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default compose(
  withDashboardSidebar,
  withRouter,
)(PracticeTests);
